/**
 * Main-thread services the account screens need (sign-in, subscription, connectors, devices): the API
 * client, the session functions bound to the engine, and the purchase flow. The engine worker has its
 * own API client for sync, relay, the IMAP bridge and scoring.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { Engine, PurchaseFlow } from '@digester/core';
import { createApiCaller, type WebApiCaller } from '../platform/api.js';
import { webDeviceName } from '../platform/capabilities.js';
import { createWebPurchaseFlow } from '../platform/purchase.js';
import { applyEntitlement, type SessionDeps } from '../platform/session.js';

export interface AppServices {
  api: WebApiCaller;
  /** For platform/session.ts: requestCode(api, …), signIn(session, …), signOut(session), refresh(session). */
  session: SessionDeps;
  purchase: PurchaseFlow;
}

export function createAppServices(engine: Engine, apiBase: string = import.meta.env.VITE_API_BASE ?? ''): AppServices {
  const api = createApiCaller({
    base: apiBase,
    auth: {
      signedIn: async () => (await engine.settings.getLocal()).account !== null,
      installToken: async () => (await engine.settings.getLocal()).installToken,
      setInstallToken: (token) => engine.settings.setLocal('installToken', token),
    },
  });
  const session: SessionDeps = { engine, api, deviceName: webDeviceName() };
  const purchase = createWebPurchaseFlow({
    api,
    returnUrl: typeof location === 'undefined' ? '/' : `${location.origin}/subscribe`,
    onEntitlement: async (entitlement) => {
      await applyEntitlement(session, entitlement);
    },
  });
  return { api, session, purchase };
}

const ServicesContext = createContext<AppServices | null>(null);

export function ServicesProvider({ services, children }: { services: AppServices; children: ReactNode }) {
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

export function useServices(): AppServices {
  const value = useContext(ServicesContext);
  if (!value) throw new Error('useServices must be used inside <ServicesProvider>');
  return value;
}
